import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { EcoEfficiencyService } from "../service/eco-efficiency.service";
import {
  EconomicIndicator,
  EconomicIndicatorComponent,
} from "../models/dto/response/ResponseCreateEconomicIndicators";
import {
  ComponentPlan,
  EcoEfficiency,
  EmissionPlan,
} from "../models/dto/response/ResponseEcoEfficiency";
import dayjs, { Dayjs } from "dayjs";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import alertService from "../components/alert/alertService";
import { useTranslation } from "react-i18next";
import { common, input } from "../i18n";
import { PercentChangeRequest, PreCalComponent } from "../models/EcoEfficiency";

export type AddEcoEfficiencySection =
  | "Details"
  | "Plan-Setting"
  | "Add-Actual-Data";

export type AddEcoEfficiencyFormValue = {
  type: "organization" | "sub-organizations";
  organizationRef: string;
  startDate: Dayjs;
  endDate: Dayjs;
  indicatorRef: string;
  suborganizationRef: string;
  environmentalImpactType: string;
  baseYear: string;
  remark: string;
  [key: string]: any;
};

type EcoEfficiencyState = {
  modalIsOpen: boolean;
  setModalIsOpen: Dispatch<SetStateAction<boolean>>;
  baseYearOptions: number[];
  setBaseYearOptions: Dispatch<SetStateAction<number[]>>;
  environmentImpactTypeOptions: string[];
  economicIndicators: EconomicIndicator[];
  getBaseYear: (
    refId: string,
    month: number,
    startYear: number,
    suborganizationRef?: string,
  ) => Promise<void>;
  ecoEfficiency: EcoEfficiency[];
  setEcoEfficiency: Dispatch<SetStateAction<EcoEfficiency[]>>;
  editData: EcoEfficiency | undefined;
  setEditData: Dispatch<SetStateAction<EcoEfficiency | undefined>>;
  filterSubOrganization: string[] | undefined;
  setFilterSubOrganization: Dispatch<SetStateAction<string[] | undefined>>;
  masterEconomicIndicators: EconomicIndicator[];
  section: AddEcoEfficiencySection;
  setSection: Dispatch<SetStateAction<AddEcoEfficiencySection>>;
  getEcoEfficiency: () => Promise<void>;
  planSettingNormalization: {
    emissionPlans: EmissionPlan[];
    componentPlans: ComponentPlan[];
  };
  setPlanSettingNormalization: Dispatch<
    SetStateAction<{
      emissionPlans: EmissionPlan[];
      componentPlans: ComponentPlan[];
    }>
  >;

  addEcoEfficiencyForm: UseFormReturn<AddEcoEfficiencyFormValue>;
  exitModal: () => Promise<void>;
  percentChangesForm: UseFormReturn<FieldValues, any, FieldValues>;
  rawDataForm: UseFormReturn<FieldValues, any, FieldValues>;
  calEcoEfficiencyPlans: (
    rows: EconomicIndicatorComponent[],
    fiveYearForecast: number[],
  ) => Promise<void>;

  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
};

export const Context = createContext<EcoEfficiencyState>(
  {} as EcoEfficiencyState,
);

export const EcoEfficiencyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const [baseYearOptions, setBaseYearOptions] = useState<number[]>([]);

  const [environmentImpactTypeOptions, setEnvironmentImpactTypeOptions] =
    useState<string[]>([]);

  const [ecoEfficiency, setEcoEfficiency] = useState<EcoEfficiency[]>([]);

  const [editData, setEditData] = useState<EcoEfficiency | undefined>();

  const [filterSubOrganization, setFilterSubOrganization] = useState<
    string[] | undefined
  >(undefined);

  const [economicIndicators, setEconomicIndicators] = useState<
    EconomicIndicator[]
  >([]);

  const [masterEconomicIndicators, setMasterEconomicIndicators] = useState<
    EconomicIndicator[]
  >([]);

  const [section, setSection] = useState<AddEcoEfficiencySection>("Details");

  const [searchText, setSearchText] = useState<string>("");

  const [planSettingNormalization, setPlanSettingNormalization] = useState<{
    emissionPlans: EmissionPlan[];
    componentPlans: ComponentPlan[];
  }>({ componentPlans: [], emissionPlans: [] });

  const getEcoEfficiency = async () => {
    try {
      const result = await EcoEfficiencyService.getEcoEfficiency();

      if (result) {
        setEcoEfficiency(result);
      }
    } catch {
      // error alert already shown by service
    }
  };

  const getBaseYear = async (
    refId: string,
    startMonth: number,
    startYear: number,
    suborganizationRef?: string,
  ) => {
    try {
      const result = await EcoEfficiencyService.getBaseYear(
        refId,
        startMonth,
        startYear,
        suborganizationRef,
      );

      if (result) {
        setBaseYearOptions(result);
      }
    } catch {
      // error alert already shown by service
    }
  };

  const getEnvironmentalImpactTypes = async () => {
    try {
      const result = await EcoEfficiencyService.getEnvironmentalImpactTypes();

      if (result) {
        setEnvironmentImpactTypeOptions(result);
      }
    } catch {
      // error alert already shown by service
    }
  };

  const getEconomicIndicators = async () => {
    try {
      const result = await EcoEfficiencyService.getEconomicIndicators();

      if (result) {
        setEconomicIndicators(result.filter((i) => i.isDisable !== true));
        setMasterEconomicIndicators(result);
      }
    } catch {
      // error alert already shown by service
    }
  };

  const addEcoEfficiencyForm = useForm<AddEcoEfficiencyFormValue>({
    reValidateMode: "onChange",
    mode: "all",
    defaultValues: {
      type: "organization",
      // organization: "",
      suborganizationRef: "",
      startDate: dayjs(`${dayjs().year()}-01-01`),
      endDate: dayjs(`${dayjs().year()}-12-01`),
      indicatorRef: "",
      baseYear: "",
      environmentalImpactType: "Emission Scope 1-3",
      remark: "",
    },
  });

  const percentChangesForm = useForm({
    reValidateMode: "onChange",
    mode: "all",
  });

  const rawDataForm = useForm({
    reValidateMode: "onChange",
    mode: "all",
  });

  const exitModal = async () => {
    let isConfirm = true;
    if (
      section === "Details" &&
      addEcoEfficiencyForm.formState.isDirty &&
      !addEcoEfficiencyForm.formState.isSubmitSuccessful
    ) {
      isConfirm = await alertService.confirm(
        t("Back"),
        t("Are-you-sure-you-want-to-close-this-page"),
        t("Yes"),
        undefined,
        undefined,
        undefined,
      );
    } else if (
      section === "Plan-Setting" &&
      (rawDataForm.formState.isDirty || percentChangesForm.formState.isDirty) &&
      !rawDataForm.formState.isSubmitSuccessful
    ) {
      isConfirm = await alertService.confirm(
        t("Back"),
        t("Are-you-sure-you-want-to-close-this-page"),
        t("Yes"),
        undefined,
        undefined,
        undefined,
      );
    }

    if (isConfirm) {
      setEditData(undefined);
      addEcoEfficiencyForm.reset();
      rawDataForm.reset();
      rawDataForm.unregister();
      percentChangesForm.reset();
      percentChangesForm.unregister();
      setSection("Details");
      setModalIsOpen(false);
    }
  };

  const calEcoEfficiencyPlans = async (
    rows: EconomicIndicatorComponent[],
    fiveYearForecast: number[],
  ) => {
    if (editData) {
      const requestBody: PercentChangeRequest = {
        components: rows
          .filter((c) => c._id !== "TOTAL")
          .map((c, i) => {
            const isUsePercent = percentChangesForm.getValues(
              `${c._id}-activated`,
            );

            return isUsePercent
              ? {
                  componentRef: c._id,
                  percentChanges: fiveYearForecast.map((y) => {
                    const rateType = percentChangesForm.getValues(
                      `${c._id}-rate-Type`,
                    );

                    const number: number =
                      Number(
                        percentChangesForm.getValues(`${c._id}-${y}-forecast`),
                      ) ?? 0;

                    return Number.isNaN(number)
                      ? 0
                      : rateType === "increase"
                      ? number
                      : number * -1;
                  }),
                }
              : {
                  componentRef: c._id,
                  customValue: fiveYearForecast.map((y) => {
                    const number: number =
                      Number(rawDataForm.getValues(`${c._id}-${y}-forecast`)) ??
                      0;

                    return Number.isNaN(number) ? 0 : number;
                  }),
                };
          }),
        emissions:
          percentChangesForm.getValues(`TOTAL-activated`) ||
          percentChangesForm.getValues(`TOTAL-activated`) === undefined
            ? {
                percentChanges: fiveYearForecast.map((y) => {
                  const number: number =
                    Number(
                      `${percentChangesForm.getValues(
                        `${"total"}-${y}-forecast`,
                      )}`,
                    ) ?? 0;

                  return Number.isNaN(number) ? 0 : number * -1;
                }),
              }
            : {
                customValue: fiveYearForecast.map((y) => {
                  const number: number =
                    Number(
                      `${rawDataForm.getValues(`${"total"}-${y}-forecast`)}`,
                    ) ?? 0;

                  return Number.isNaN(number) ? 0 : number;
                }),
              },
      };

      let result;
      try {
        result = await EcoEfficiencyService.calEcoEfficiencyPlans(
          editData._id,
          requestBody,
        );
      } catch {
        // error alert already shown by service
        return;
      }

      if (result) {
        try {
          const planSettingNormalization: {
            emissionPlans: EmissionPlan[];
            componentPlans: ComponentPlan[];
          } = {
            emissionPlans: [
              ...result.success.emission.map((y) => {
                return {
                  _id: "emission",
                  id: "emission",
                  year: y.year,
                  amount: y.amount,
                  normalizedValue: y.normalizedValue,
                  ecoefficienciesRef: "emission",
                  percentChange: y.percentChange,
                };
              }),
            ],
            componentPlans: [
              ...result.success.components.map((e) => {
                return {
                  componentRef: e.componentRef,
                  nameEn: "string",
                  nameTh: "string",
                  unit: "string",
                  plans: [
                    ...e.amounts.map((p) => {
                      return {
                        _id: e.componentRef,
                        year: p.year,
                        amount: p.amount,
                        normalizedValue: p.normalizedValue,
                        valueType: "string",
                        ecoefficienciesRef: "string",
                        economiccomponentsRef: "string",
                        id: "string",
                        percentChange: p.percentChange,
                      };
                    }),
                  ],
                };
              }),
            ],
          };
          setPlanSettingNormalization(planSettingNormalization);

          result.success.components.forEach((c, i) => {
            let increase = 0;
            let decrease = 0;
            let first = "";

            c.amounts.forEach((y) => {
              y.percentChange >= 0 ? increase++ : decrease++;

              if (i === 0)
                first = y.percentChange >= 0 ? "increase" : "decrease";

              const key = `${c.componentRef}-${y.year}-forecast`;

              rawDataForm.setValue(key, y.amount);

              percentChangesForm.setValue(
                key,
                y.percentChange >= 0
                  ? y.percentChange
                  : Math.abs(y.percentChange),
                {
                  shouldValidate: false,
                  shouldDirty: false,
                  shouldTouch: false,
                },
              );
            });

            percentChangesForm.setValue(
              `${c.componentRef}-rate-Type`,
              increase === decrease
                ? first
                : increase > decrease
                ? "increase"
                : "decrease",
            );
          });
          result.success.emission.forEach((y) => {
            const key = `total-${y.year}-forecast`;
            rawDataForm.setValue(key, y.amount);
            percentChangesForm.setValue(key, y.percentChange * -1);
          });
        } catch (error) {
          console.error(error);
        }
      }
    }
  };

  useEffect(() => {
    getEcoEfficiency();
    getEnvironmentalImpactTypes();
    getEconomicIndicators();
  }, []);

  return (
    <Context.Provider
      value={{
        modalIsOpen,
        setModalIsOpen,
        baseYearOptions,
        environmentImpactTypeOptions,
        economicIndicators,
        getBaseYear,
        setBaseYearOptions,
        ecoEfficiency,
        setEcoEfficiency,
        editData,
        setEditData,
        filterSubOrganization,
        setFilterSubOrganization,
        masterEconomicIndicators,
        section,
        setSection,
        getEcoEfficiency,
        planSettingNormalization,
        setPlanSettingNormalization,
        addEcoEfficiencyForm,
        exitModal,
        percentChangesForm,
        rawDataForm,
        calEcoEfficiencyPlans,
        searchText,
        setSearchText,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const EcoEfficiencyContext = () => useContext(Context);
